import type { FeatureModeStore } from "./FeatureModeStore";
import type { BetSystem } from "../bet/BetSystemTypes";
import type { LocalizationService } from "../localization/LocalizationService";
import type { BetPort } from "../bet/BetPort";
import type { FeatureModeBadgePort, FeatureModeDisableButtonPort } from "./FeatureModePorts";
import type { PresentationTaskRunner } from "@slot/presentation";

/**
 * How long the badge stays on screen after a feature mode becomes active.
 * The underlying feature mode (and its bet style + disable button) persists
 * indefinitely; only the badge itself auto-hides.
 */
const BADGE_AUTO_HIDE_MS = 4000;

/** Duration of the opacity fade when the badge auto-hides. */
const BADGE_FADE_OUT_SECONDS = 0.5;

export interface FeatureModeUiPresenterDeps {
  featureModeStore: FeatureModeStore;
  betSystem: BetSystem;
  chromeTaskRunner: PresentationTaskRunner;
  localization: LocalizationService;
}

export class FeatureModeUiPresenter {
  private unsubscribeFeatureMode?: () => void;
  private unsubscribeBet?: () => void;

  private lastSeenActiveModeId: string | null = null;
  private badgeAutoHidden = false;
  /** Bumped to cancel a pending auto-hide; the delay rides the chrome runner. */
  private badgeHideGeneration = 0;

  public constructor(
    private readonly deps: FeatureModeUiPresenterDeps,
    private readonly input: {
      badgePanel: FeatureModeBadgePort | null;
      disableButtonPanel: FeatureModeDisableButtonPort | null;
      betPanel: BetPort | null;
    },
  ) {
    this.input.disableButtonPanel?.bind({
      onDisable: () => {
        this.deps.featureModeStore.clear();
      },
    });
  }

  public start(): void {
    this.unsubscribeFeatureMode = this.deps.featureModeStore.subscribe(() => this.render());
    this.unsubscribeBet = this.deps.betSystem.subscribe(() => this.render());
    this.render();
  }

  public stop(): void {
    this.unsubscribeFeatureMode?.();
    this.unsubscribeFeatureMode = undefined;

    this.unsubscribeBet?.();
    this.unsubscribeBet = undefined;

    this.clearBadgeHideTimer();
  }

  public render(): void {
    const activeMode = this.deps.featureModeStore.getSnapshot().activeMode;
    const isFeatureModeActive = !!activeMode;
    const isBetLocked = this.deps.betSystem.isLocked();

    const currentModeId = activeMode?.id ?? null;
    if (currentModeId !== this.lastSeenActiveModeId) {
      this.lastSeenActiveModeId = currentModeId;
      this.badgeAutoHidden = false;
      this.clearBadgeHideTimer();

      if (currentModeId !== null) {
        const generation = this.badgeHideGeneration;
        void this.deps.chromeTaskRunner.sleep(BADGE_AUTO_HIDE_MS / 1000).then(() => {
          if (generation !== this.badgeHideGeneration) return;
          this.badgeAutoHidden = true;
          this.input.badgePanel?.fadeOut(BADGE_FADE_OUT_SECONDS);
        });
      }
    }

    if (!this.badgeAutoHidden) {
      // setVisible(true) resets opacity to 255 + activates the root; on
      // mode-clear setVisible(false) hides instantly (no fade). The auto-
      // hide branch above is the only path that animates the fade.
      this.input.badgePanel?.setVisible(isFeatureModeActive);
    }

    if (activeMode) {
      this.input.badgePanel?.setTitle(
        this.deps.localization.t(activeMode.titleKey, activeMode.fallbackTitle),
      );
    }

    this.input.disableButtonPanel?.setVisible(isFeatureModeActive);
    this.input.disableButtonPanel?.setInteractable(!isBetLocked);

    this.input.betPanel?.setFeatureModeActive(isFeatureModeActive);
  }

  private clearBadgeHideTimer(): void {
    this.badgeHideGeneration++;
  }
}